/**
 * How long an SMS body is and how many billed parts it takes — the editor's
 * counter ("n / 160 · 1 part") and the save gate (≤ 3 parts).
 *
 *  - GSM-7 when every character is in the GSM 03.38 basic set or its
 *    extension table; an extension character (`\f ^ { } \ [ ~ ] | €`) costs
 *    two septets. One part holds 160 septets; a longer body is cut into parts
 *    of 153, and an escape pair is never split across two parts (so
 *    152 a + € + 305 a is 4 parts, not ceil(459 / 153) = 3 — audit r1 M9).
 *  - UCS-2 otherwise; the length is in UTF-16 code units (an emoji is two).
 *    One part holds 70; a longer body is cut into parts of 67, and a
 *    surrogate pair is never split either.
 *  - An empty body has length 0 and 0 parts.
 *
 * MIRROR: main-consumer `src/lib/sms-length.ts` (the writer — what the
 * company is billed by), main-backend Java `SmsLength` (the save gate). All
 * three are pinned against the same cases: `src/lib/__fixtures__/sms-length.json`
 * here, a byte-identical copy of the consumer's SOURCE fixture
 * (`check-fixture-parity.sh`).
 *
 * The counter measures the body AS TYPED, placeholders included (`{{eventName}}`
 * counts its braces as extension characters) — exactly what the backend's save
 * gate measures. The rendered SMS can be longer or shorter; the consumer
 * re-measures after rendering and skips with `body-too-long`.
 */

export type SmsEncoding = "GSM-7" | "UCS-2";

export interface SmsMeasure {
  encoding: SmsEncoding;
  /** Septets (GSM-7) or UTF-16 code units (UCS-2). */
  length: number;
  parts: number;
  /** UCS-2 only: the first character that left the GSM-7 alphabet. */
  firstNonGsm?: string;
}

/** Most parts a body may take (consumer env `SMS_MAX_PARTS`, backend `smsBodyTooLong`). */
export const SMS_MAX_PARTS = 3;

/**
 * GSM 03.38 basic character set, without the escape (0x1B) itself. Real
 * characters on purpose (house rule: no escape sequences for non-ASCII).
 */
const GSM_BASIC =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?" +
  "¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";

/** GSM 03.38 extension table: each costs the escape plus itself, 2 septets. */
const GSM_EXTENSION = "\f^{}\\[~]|€";

const BASIC = new Set(Array.from(GSM_BASIC));
const EXTENSION = new Set(Array.from(GSM_EXTENSION));

/** Characters one part holds, per encoding (single part / each part of a longer body). */
export const SMS_PART_SIZE: Record<SmsEncoding, { single: number; multi: number }> = {
  "GSM-7": { single: 160, multi: 153 },
  "UCS-2": { single: 70, multi: 67 },
};

export function measureSms(body: string): SmsMeasure {
  if (body.length === 0) return { encoding: "GSM-7", length: 0, parts: 0 };
  const septetCosts: number[] = [];
  let firstNonGsm: string | undefined;
  for (const ch of body) {
    if (BASIC.has(ch)) septetCosts.push(1);
    else if (EXTENSION.has(ch)) septetCosts.push(2);
    else {
      firstNonGsm = ch;
      break;
    }
  }
  if (firstNonGsm === undefined) {
    const septets = septetCosts.reduce((a, b) => a + b, 0);
    return { encoding: "GSM-7", length: septets, parts: partsOf("GSM-7", septets, septetCosts) };
  }
  // A code point is one UTF-16 unit, or two for a surrogate pair (an emoji).
  const unitCosts = Array.from(body, (ch) => ch.length);
  const units = body.length;
  return { encoding: "UCS-2", length: units, parts: partsOf("UCS-2", units, unitCosts), firstNonGsm };
}

function partsOf(encoding: SmsEncoding, length: number, costs: readonly number[]): number {
  const size = SMS_PART_SIZE[encoding];
  return length <= size.single ? 1 : countSegments(costs, size.multi);
}

/**
 * How many parts of at most `size` units the characters take when a
 * character (an escape pair, a surrogate pair) is never split across two parts.
 */
function countSegments(costs: readonly number[], size: number): number {
  let segments = 1;
  let used = 0;
  for (const cost of costs) {
    if (used + cost > size) {
      segments += 1;
      used = 0;
    }
    used += cost;
  }
  return segments;
}

/**
 * The capacity the counter shows the length against: one part's size while
 * the body fits one part, else the size of all parts used so far ("170 / 306").
 */
export function smsCapacity(m: Pick<SmsMeasure, "encoding" | "parts">): number {
  const size = SMS_PART_SIZE[m.encoding];
  return m.parts <= 1 ? size.single : m.parts * size.multi;
}
